// Budget upload history for the Day Zero page.
//
// A Day Zero upload wipes the version's 'Day Zero Budget' rows and reinserts
// one row per budget line, so PC.Forecast_Audit_Log sees it as a burst of
// INSERT rows from one person on one version within a few seconds. Listing
// those rows one by one (the way the per-tab history panels do) would show a
// 400-line upload as 400 identical lines. This helper folds the burst back into
// the upload it was, and compares each upload with the previous one on the
// same version, so the page can say "Sep 30, Seth, 412 lines, cost +$84k vs
// the Sep 12 upload: 6 lines added, 2 removed, 14 changed".
//
// Single-cell edits (UPDATE) and row deletes (DELETE) on the tab are kept too,
// grouped the same way, so a hand edit between uploads is not lost.

export const DAY_ZERO_AUDIT_TAB = 'Day Zero Budget';

// Rows further apart than this are separate events. An upload of several
// hundred lines is one updateUserDataArray call, so its audit rows share a
// GETDATE() to within a second or two; a minute leaves room for a slow server.
const EVENT_GAP_MS = 60 * 1000;

const num = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const n = Number(String(v).replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
};

const parseJson = (s) => {
    if (!s) return null;
    if (typeof s === 'object') return s;
    try { return JSON.parse(s); } catch (e) { return null; }
};

// Audit_Datetime comes back as a string; compare as epoch millis. The driver
// labels Central wall-clock as 'Z', but the error is the same on every row, so
// differences between rows are still right.
const whenMs = (r) => {
    const t = Date.parse(r.Audit_Datetime);
    return isNaN(t) ? 0 : t;
};

const who = (r) => r.User_Email || r.User_Name || r.User_ID || '';

// Revenue lines are the 4xxxx cost types; everything else is cost.
export const isRevenueType = (costType) => String(costType || '').trim().startsWith('4');

export const lineKey = (code, type) => `${String(code || '').trim()}|${String(type || '').trim()}`;

const lineFromRow = (row) => ({
    code: String(row.Cost_Code || '').trim(),
    type: String(row.Cost_Type || '').trim(),
    resource: String(row.Resource_Code || '').trim(),
    amount: num(row.Value_Amount != null && row.Value_Amount !== '' ? row.Value_Amount : row.Forecast_Value),
    hours: num(row.Hours),
    quantity: num(row.Quantity),
});

const addLine = (lines, line) => {
    const key = lineKey(line.code, line.type);
    const cur = lines.get(key);
    if (cur) {
        cur.amount += line.amount;
        cur.hours += line.hours;
        cur.quantity += line.quantity;
        cur.count += 1;
        if (line.resource && !cur.resources.includes(line.resource)) cur.resources.push(line.resource);
    } else {
        lines.set(key, { key, code: line.code, type: line.type, amount: line.amount, hours: line.hours, quantity: line.quantity, count: 1, resources: line.resource ? [line.resource] : [] });
    }
};

const sumLines = (lines) => {
    let cost = 0, revenue = 0, hours = 0;
    lines.forEach((l) => {
        if (isRevenueType(l.type)) revenue += l.amount; else cost += l.amount;
        hours += l.hours;
    });
    return { cost, revenue, hours };
};

/**
 * Folds audit rows (any order) into events, newest first.
 *
 * An event is consecutive rows with the same person, version and action kind,
 * none more than EVENT_GAP_MS apart. Kind is 'upload' for INSERT bursts, 'edit'
 * for UPDATE and DELETE rows.
 */
export function groupBudgetEvents(rows) {
    const sorted = [...(rows || [])].sort((a, b) => Number(b.Audit_ID) - Number(a.Audit_ID));
    const events = [];
    let cur = null;
    for (const r of sorted) {
        const kind = r.Action_Type === 'INSERT' ? 'upload' : 'edit';
        const t = whenMs(r);
        const sameEvent = cur
            && cur.kind === kind
            && cur.user === who(r)
            && String(cur.version) === String(r.Version_GUID)
            && Math.abs(cur.firstMs - t) <= EVENT_GAP_MS;
        if (!sameEvent) {
            cur = {
                id: String(r.Audit_ID),
                kind,
                user: who(r),
                userName: r.User_Name || '',
                version: r.Version_GUID,
                job: r.BU_ID,
                // Rows arrive newest first, so the last row seen is the earliest.
                when: r.Audit_Datetime,
                firstMs: t,
                rows: [],
                lines: new Map(),
                changes: [],
            };
            events.push(cur);
        }
        cur.rows.push(r);
        cur.when = r.Audit_Datetime;
        cur.firstMs = t;
        if (kind === 'upload') {
            const next = parseJson(r.New_Row_JSON);
            if (next) addLine(cur.lines, lineFromRow(next));
        } else {
            const prevRow = parseJson(r.Prev_Row_JSON) || {};
            const newRow = parseJson(r.New_Row_JSON) || prevRow;
            cur.changes.push({
                id: String(r.Audit_ID),
                action: r.Action_Type,
                code: String(newRow.Cost_Code || prevRow.Cost_Code || '').trim(),
                type: String(newRow.Cost_Type || prevRow.Cost_Type || '').trim(),
                field: r.Forecast_Field_Name || '',
                prev: r.Prev_Value,
                next: r.New_Value,
            });
        }
    }
    for (const e of events) {
        e.lineCount = e.rows.length;
        e.totals = sumLines(e.lines);
        delete e.firstMs;
    }
    return events;
}

/**
 * Compares an upload's lines with the previous upload's, keyed by cost code and
 * cost type. Returns the added, removed and changed lines and the net change in
 * cost and revenue. `prev` may be null (first upload on the version).
 */
export function diffBudgetLines(curr, prev) {
    const added = [], removed = [], changed = [];
    const prevLines = prev ? prev.lines : new Map();
    curr.lines.forEach((l, key) => {
        const p = prevLines.get(key);
        if (!p) { added.push({ ...l, before: 0, after: l.amount, delta: l.amount }); return; }
        const amountMoved = Math.abs(l.amount - p.amount) >= 0.005;
        const hoursMoved = Math.abs(l.hours - p.hours) >= 0.005;
        const qtyMoved = Math.abs(l.quantity - p.quantity) >= 0.005;
        if (amountMoved || hoursMoved || qtyMoved) {
            changed.push({ ...l, before: p.amount, after: l.amount, delta: l.amount - p.amount, hoursBefore: p.hours, hoursAfter: l.hours, qtyBefore: p.quantity, qtyAfter: l.quantity });
        }
    });
    prevLines.forEach((p, key) => {
        if (!curr.lines.has(key)) removed.push({ ...p, before: p.amount, after: 0, delta: -p.amount });
    });
    const byAmount = (a, b) => Math.abs(b.delta) - Math.abs(a.delta);
    added.sort(byAmount); removed.sort(byAmount); changed.sort(byAmount);
    const prevTotals = prev ? prev.totals : { cost: 0, revenue: 0, hours: 0 };
    return {
        added, removed, changed,
        costDelta: curr.totals.cost - prevTotals.cost,
        revenueDelta: curr.totals.revenue - prevTotals.revenue,
        hoursDelta: curr.totals.hours - prevTotals.hours,
        first: !prev,
    };
}

/**
 * Attaches to each upload event the previous upload on the same version and the
 * diff against it. Events are newest first, so "previous" is the next upload
 * event in the list with the same version. The oldest upload on a version is
 * compared with nothing unless `truncated` says older rows exist, in which case
 * it may itself be incomplete and its diff is left out.
 */
export function attachBudgetDiffs(events, { truncated = false } = {}) {
    const out = events.map((e) => ({ ...e }));
    for (let i = 0; i < out.length; i++) {
        const e = out[i];
        if (e.kind !== 'upload') continue;
        let prev = null;
        for (let j = i + 1; j < out.length; j++) {
            if (out[j].kind === 'upload' && String(out[j].version) === String(e.version)) { prev = out[j]; break; }
        }
        const isOldest = i === out.length - 1;
        e.partial = truncated && isOldest;
        e.diff = (!prev && truncated) || e.partial ? null : diffBudgetLines(e, prev);
        e.prevId = prev ? prev.id : null;
    }
    return out;
}
